import { Stack } from '@mui/material'
import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { GapBox } from 'components/Box'
import { BoxForm, NumberField, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import { TextLink } from 'components/Link'
import Paper from 'components/Paper'
import { RelatedList, RelatedListItem, RelatedSettings } from 'components/RelatedSettings'
import { useGetWorkspacePrebidConfiguration } from 'features/globalSetup'
import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { ROUTES } from 'routes'
import { Schema } from '../../schema'
import { KNOWLEDGE_BASE_URL } from './constants'

export const TimeoutSection = (): JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation(['features/domain', 'features/globalSetup', 'common'])
  const { workspaceId } = useWorkspaceParam()
  const { domain } = useContext(AddEditDomainContext)
  const { workspacePrebidConfiguration } = useGetWorkspacePrebidConfiguration({
    workspaceId,
  })
  const { control, watch } = useFormContext<Schema>()
  const { customPrebidTimeoutEnabled } = watch()

  const cannotUpdateDomain: boolean = ability.cannot('update', 'Domain')

  return (
    <Paper>
      <SectionHeader
        subtitle={t('form.prebidStack.timeouts.description')}
        subtitleEndAdornment={
          <TextLink
            link={{
              title: t('common:learnMore'),
              url: KNOWLEDGE_BASE_URL.PREBID_TIMEOUTS,
            }}
          />
        }
        title={t('form.prebidStack.timeouts.title')}
      />

      <GapBox />

      <Stack
        direction='row'
        spacing={2}
        sx={{ alignItems: 'flex-start' }}
      >
        <BoxForm>
          <Switch
            control={control}
            disabled={cannotUpdateDomain}
            label={t('form.prebidStack.timeouts.customPrebidTimeoutEnabled.label')}
            labelPlacement='start'
            name='customPrebidTimeoutEnabled'
            tooltip={{
              content: t('form.prebidStack.timeouts.customPrebidTimeoutEnabled.tooltip'),
              link: {
                title: t('common:learnMore'),
                url: KNOWLEDGE_BASE_URL.PREBID_TIMEOUTS,
              },
            }}
          />

          {customPrebidTimeoutEnabled && (
            <NumberField
              control={control}
              disabled={cannotUpdateDomain}
              label={t('form.prebidStack.timeouts.prebidTimeout.label')}
              name='prebidTimeout'
            />
          )}
        </BoxForm>

        {customPrebidTimeoutEnabled && domain && workspacePrebidConfiguration && (
          <RelatedSettings>
            <RelatedList>
              <RelatedListItem
                content={t('features/globalSetup:relatedSettings.prebidTimeout')}
                url={generatePath(ROUTES.GLOBAL_SETUP.DEFAULTS.ROOT, {
                  tab: 'prebid-stack',
                  workspaceId,
                })}
                value={workspacePrebidConfiguration.prebidTimeout}
              />
            </RelatedList>
          </RelatedSettings>
        )}
      </Stack>
    </Paper>
  )
}
